import { useEffect, useState } from 'react';
import { MdClose, MdRefresh } from 'react-icons/md';
import { useAdvisor } from '../context/AdvisorContext';
import { apiFetch } from '../api';

// Internal metrics surface for the operator. Reuses memory-modal CSS so it
// matches the rest of the demo-controls dialogs; only the table styles are
// metrics-specific. Fetches on open; the table refresh button reruns the
// request without closing the modal.
export default function MetricsModal() {
  const { metricsViewOpen, closeMetricsView } = useAdvisor();
  const [state, setState] = useState({ loading: false, error: '', total: 0, items: [] });

  async function load() {
    setState(s => ({ ...s, loading: true, error: '' }));
    try {
      const res = await apiFetch('/api/metrics/subscribers');
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${(await res.text()).slice(0, 200)}`);
      const json = await res.json();
      setState({ loading: false, error: '', total: json.total || 0, items: json.items || [] });
    } catch (e) {
      setState(s => ({ ...s, loading: false, error: e?.message || 'Could not load metrics.' }));
    }
  }

  useEffect(() => {
    if (!metricsViewOpen) return;
    load();
  }, [metricsViewOpen]);

  if (!metricsViewOpen) return null;

  return (
    <div className="memory-modal-backdrop" onClick={closeMetricsView}>
      <div
        className="memory-modal metrics-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="memory-modal__header">
          <div>
            <div className="memory-modal__title">Signup Metrics</div>
            <div className="memory-modal__subtitle">
              Submissions to the “Get Updates” form on purowealth.com.
            </div>
          </div>
          <button
            className="memory-modal__close"
            onClick={closeMetricsView}
            aria-label="Close"
            type="button"
          >
            <MdClose size={18} />
          </button>
        </div>

        <div className="memory-modal__body">
          <div className="metrics-modal__summary">
            <div className="metrics-modal__count">
              <span className="metrics-modal__count-num">
                {state.loading ? '…' : state.total}
              </span>
              <span className="metrics-modal__count-label">total signups</span>
            </div>
            <button
              className="metrics-modal__refresh"
              onClick={load}
              disabled={state.loading}
              title="Refresh"
              type="button"
            >
              <MdRefresh size={14} /> Refresh
            </button>
          </div>

          {state.error ? (
            <div className="memory-modal__empty">⚠️ {state.error}</div>
          ) : state.loading && state.items.length === 0 ? (
            <div className="memory-modal__empty">Loading…</div>
          ) : state.items.length === 0 ? (
            <div className="memory-modal__empty">No signups yet.</div>
          ) : (
            <table className="metrics-modal__table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Email</th>
                </tr>
              </thead>
              <tbody>
                {state.items.map((row, i) => (
                  <tr key={i}>
                    <td className="metrics-modal__when">{formatWhen(row.timestamp)}</td>
                    <td className="metrics-modal__email">{row.emailMasked || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// "2026-05-25T14:31:09.812Z" → "2026-05-25 14:31 UTC". Fallback to the raw
// string if it doesn't parse — the Lambda may return a key-derived stamp
// that's already been trimmed of milliseconds.
function formatWhen(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`;
}
